import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useLocalization } from "@fluent/react";
import { isWellFormedCategoryKey } from "byzantine/src/TransactionEnrichmentSource";
import { Button, SplitButton } from "@narmi/design_system";
import Filters from "byzantine/src/filters";

import { useAccountsContext } from "../../../../../components/contexts/AccountContext";
import {
  LIVE_LEDGER_ACCOUNT_QUERY_KEY,
  LIVE_LEDGER_AMOUNT_MAX_QUERY_KEY,
  LIVE_LEDGER_AMOUNT_MIN_QUERY_KEY,
  LIVE_LEDGER_BAI_CODES_QUERY_KEY,
  LIVE_LEDGER_CATEGORY_QUERY_KEY,
  LIVE_LEDGER_DATE_FROM_QUERY_KEY,
  LIVE_LEDGER_DATE_TO_QUERY_KEY,
  LIVE_LEDGER_METHOD_QUERY_KEY,
  LIVE_LEDGER_SEARCH_QUERY_KEY,
  LIVE_LEDGER_TYPE_QUERY_KEY,
  TYPE_FILTER_OPTIONS,
} from "../../constants";
import { useFilterContext } from "../../context/FilterContext";
import {
  findMatchingPreset,
  formatLocaleDate,
  parseFilterDate,
  TYPE_VALID_VALUES,
} from "../../helpers";
import { useLiveLedgerCategories } from "../../hooks/useLiveLedgerCategories";
import { useMultiSelectFilter } from "../../hooks/useMultiSelectFilter";
import { useRangeFilter } from "../../hooks/useRangeFilter";
import { useSearchFilter } from "../../hooks/useSearchFilter";
import { useSingleSelectFilter } from "../../hooks/useSingleSelectFilter";
import { useTransactionSavedFilters } from "../../hooks/useTransactionSavedFilters";
import DeleteViewDialog from "../../../../components/dialogs/DeleteViewDialog/DeleteViewDialog";
import SaveViewDialog from "../../../../components/dialogs/SaveViewDialog/SaveViewDialog";
import AccountFilterPanel, {
  getAccountLabel,
} from "../Filters/AccountFilterPanel";
import AmountFilterPanel, { parseNumber } from "../Filters/AmountFilterPanel";
import BaiCodesFilterPanel from "../Filters/BaiCodesFilterPanel";
import CategoryFilterPanel from "../Filters/CategoryFilterPanel";
import MethodFilterPanel from "../Filters/MethodFilterPanel";
import DateRangeFilterPanel from "../Filters/DateRangeFilterPanel";
import SavedFiltersPanel from "../Filters/SavedFiltersPanel";
import TypeFilterPanel from "../Filters/TypeFilterPanel";
import FilterBar from "../FilterBar";

import styles from "./TransactionLedgerFilters.module.scss";

import type { TransactionLedgerFilterId } from "./filterRegistry";
import type { FilterBarFilter, FilterBarSearch } from "../FilterBar";

type Cents = number & { readonly __brand: "Cents" };

export interface TransactionLedgerFiltersProps {
  filterIds: TransactionLedgerFilterId[];
  filtersShownOnlyWhenActive?: TransactionLedgerFilterId[];
  filtersAlwaysVisible: boolean;
  search: FilterBarSearch | false;
  scopedAccountIds?: string[];
  resetPage: () => void;
}

const TransactionLedgerFilters = ({
  filterIds,
  filtersShownOnlyWhenActive,
  filtersAlwaysVisible,
  search,
  scopedAccountIds,
  resetPage,
}: TransactionLedgerFiltersProps) => {
  const { l10n } = useLocalization();
  const { hasAnyActive } = useFilterContext();
  const { accounts } = useAccountsContext();

  const savedFiltersEnabled = filterIds.includes("saved-filters");
  const {
    savedFilters: savedFiltersList,
    isLoading: savedFiltersLoading,
    selectedSavedFilter,
    selectSavedFilter,
    clearSelection,
    updateSavedFilter,
    createSavedFilter,
    deleteSavedFilter,
    validateName: validateSavedFilterName,
  } = useTransactionSavedFilters(savedFiltersEnabled);

  const {
    inputValue: searchValue,
    debouncedValue: debouncedSearch,
    onChange: onSearchChange,
  } = useSearchFilter({ queryKey: LIVE_LEDGER_SEARCH_QUERY_KEY });

  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [deleteFilterId, setDeleteFilterId] = useState<string | null>(null);
  const [saveAction, setSaveAction] = useState<"save" | "update">("save");

  const handleSaveAction = useCallback(() => {
    if (saveAction === "save") {
      setIsSaveDialogOpen(true);
    } else {
      updateSavedFilter();
    }
  }, [saveAction, updateSavedFilter]);

  const prevSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    if (prevSearchRef.current === debouncedSearch) return;
    prevSearchRef.current = debouncedSearch;
    resetPage();
  }, [debouncedSearch, resetPage]);

  useEffect(() => {
    setSaveAction("save");
  }, [selectedSavedFilter?.id]);

  const handleClearFilters = useCallback(() => {
    resetPage();
    clearSelection();
  }, [clearSelection, resetPage]);

  // --- Account filter ---
  const accountIds = useMemo(
    () =>
      (accounts ?? [])
        .filter((account) => account.isInternal())
        .map((account) => account.id),
    [accounts]
  );
  const getAccountOptionLabel = useCallback(
    (id: string) => {
      const account = (accounts ?? []).find((candidate) => candidate.id === id);
      return account ? getAccountLabel(account) : undefined;
    },
    [accounts]
  );
  const {
    selectedValues: filterSelectedAccountIds,
    setSelectedValues: setSelectedAccountIds,
    chipLabel: accountsChipLabel,
    chipCount: accountsChipCount,
  } = useMultiSelectFilter({
    queryKey: LIVE_LEDGER_ACCOUNT_QUERY_KEY,
    validValues: accountIds,
    getOptionLabel: getAccountOptionLabel,
  });
  const selectedAccountIds = scopedAccountIds ?? filterSelectedAccountIds;

  // --- Category filter ---
  const {
    validSlugs: categorySlugs,
    labelForSlug,
    isAvailableSlug,
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useLiveLedgerCategories();
  // Availability is unknown until the tree loads, and unknowable if it failed.
  const categoriesKnown = !categoriesLoading && !categoriesError;
  const getCategoryOptionLabel = useCallback(
    (slug: string) =>
      !categoriesKnown || isAvailableSlug(slug)
        ? labelForSlug(slug)
        : l10n.getString("live-ledger-filter-category-unavailable"),
    [categoriesKnown, isAvailableSlug, labelForSlug, l10n]
  );
  const {
    selectedValues: selectedCategories,
    setSelectedValues: setSelectedCategories,
    chipLabel: categoryChipLabel,
    chipCount: categoryChipCount,
  } = useMultiSelectFilter({
    queryKey: LIVE_LEDGER_CATEGORY_QUERY_KEY,
    validValues: categoriesKnown ? categorySlugs : undefined,
    getOptionLabel: getCategoryOptionLabel,
    keepUnavailableValues: true,
    isWellFormed: isWellFormedCategoryKey,
  });

  // --- Method filter ---
  const {
    selectedValues: selectedMethods,
    setSelectedValues: setSelectedMethods,
  } = useMultiSelectFilter({
    queryKey: LIVE_LEDGER_METHOD_QUERY_KEY,
  });

  // --- BAI codes filter ---
  const {
    selectedValues: selectedBaiCodes,
    setSelectedValues: setSelectedBaiCodes,
  } = useMultiSelectFilter({
    queryKey: LIVE_LEDGER_BAI_CODES_QUERY_KEY,
  });

  // --- Amount filter ---
  const {
    min: amountMin,
    max: amountMax,
    clearRange: clearAmountRange,
    chipLabel: amountChipLabel,
  } = useRangeFilter<number>({
    queryKeyMin: LIVE_LEDGER_AMOUNT_MIN_QUERY_KEY,
    queryKeyMax: LIVE_LEDGER_AMOUNT_MAX_QUERY_KEY,
    parseValue: parseNumber,
    serializeValue: String,
    formatLabel: (min, max) => {
      const fmtMin =
        min !== undefined ? Filters.currency(min as Cents) : undefined;
      const fmtMax =
        max !== undefined ? Filters.currency(max as Cents) : undefined;
      if (fmtMin && fmtMax) return `${fmtMin} – ${fmtMax}`;
      if (fmtMin) return `>= ${fmtMin}`;
      if (fmtMax) return `<= ${fmtMax}`;
      return undefined;
    },
  });

  // --- Type filter ---
  const getTypeLabel = useCallback(
    (value: string) => {
      const option = TYPE_FILTER_OPTIONS.find((o) => o.value === value);
      return option ? l10n.getString(option.ftlKey) : undefined;
    },
    [l10n]
  );
  const {
    selectedValue: selectedType,
    setSelectedValue: setSelectedType,
    chipLabel: typeChipLabel,
  } = useSingleSelectFilter({
    queryKey: LIVE_LEDGER_TYPE_QUERY_KEY,
    validValues: TYPE_VALID_VALUES,
    getOptionLabel: getTypeLabel,
  });

  // --- Date range filter ---
  const {
    min: settledFrom,
    max: settledTo,
    clearRange: clearDateRange,
    chipLabel: dateChipLabel,
  } = useRangeFilter<string>({
    queryKeyMin: LIVE_LEDGER_DATE_FROM_QUERY_KEY,
    queryKeyMax: LIVE_LEDGER_DATE_TO_QUERY_KEY,
    parseValue: parseFilterDate,
    serializeValue: (v) => v,
    formatLabel: useCallback(
      (from: string | undefined, to: string | undefined) => {
        if (!from || !to) return undefined;
        const matchingPreset = findMatchingPreset(from, to);
        if (matchingPreset) {
          return l10n.getString(matchingPreset.ftlId);
        }
        return `${formatLocaleDate(from)} – ${formatLocaleDate(to)}`;
      },
      [l10n]
    ),
  });

  // --- Build filter chip config ---
  const availableFilters = useMemo<FilterBarFilter[]>(() => {
    const savedFiltersLabel = l10n.getString(
      "live-ledger-filter-saved-filters"
    );
    const dateRangeLabel = l10n.getString("live-ledger-filter-date-range");
    return [
      {
        id: "saved-filters",
        label: savedFiltersLabel,
        labelOverride: selectedSavedFilter?.name,
        startIcon: "bookmark",
        alwaysVisible: true,
        renderPanel: (close) => (
          <SavedFiltersPanel
            savedFilters={savedFiltersList}
            isLoading={savedFiltersLoading}
            onSelectSavedFilter={selectSavedFilter}
            onDeleteSavedFilter={setDeleteFilterId}
            close={close}
          />
        ),
      },
      {
        id: "accounts",
        label: l10n.getString("live-ledger-filter-accounts"),
        labelOverride: accountsChipLabel,
        startIcon: "bank",
        count: accountsChipCount,
        alwaysVisible: true,
        isActive: selectedAccountIds.length > 0,
        onRemove: () => setSelectedAccountIds([]),
        renderPanel: (close, remove) => (
          <AccountFilterPanel
            close={close}
            remove={remove}
            onApply={resetPage}
          />
        ),
      },
      {
        id: "date-range",
        label: dateRangeLabel,
        labelOverride: dateChipLabel,
        startIcon: "calendar",
        minBreakpoint: "*" as const,
        alwaysVisible: true,
        isActive: settledFrom !== undefined || settledTo !== undefined,
        onRemove: clearDateRange,
        renderPanel: (close) => (
          <DateRangeFilterPanel close={close} onApply={resetPage} />
        ),
      },
      {
        id: "category",
        label: l10n.getString("live-ledger-filter-category"),
        labelOverride: categoryChipLabel,
        startIcon: "filter",
        count: categoryChipCount,
        isActive: selectedCategories.length > 0,
        onRemove: () => setSelectedCategories([]),
        renderPanel: (close, remove) => (
          <CategoryFilterPanel
            close={close}
            remove={remove}
            onApply={resetPage}
          />
        ),
      },
      {
        id: "method",
        label: l10n.getString("live-ledger-filter-method"),
        startIcon: "transfer-arrows",
        isActive: selectedMethods.length > 0,
        count: selectedMethods.length || undefined,
        onRemove: () => setSelectedMethods([]),
        renderPanel: (close, remove) => (
          <MethodFilterPanel
            close={close}
            remove={remove}
            onApply={resetPage}
          />
        ),
      },
      {
        id: "bai-codes",
        label: l10n.getString("live-ledger-filter-bai-code"),
        startIcon: "list",
        isActive: selectedBaiCodes.length > 0,
        count: selectedBaiCodes.length || undefined,
        onRemove: () => setSelectedBaiCodes([]),
        renderPanel: (close, remove) => (
          <BaiCodesFilterPanel
            close={close}
            remove={remove}
            onApply={resetPage}
          />
        ),
      },
      {
        id: "type",
        label: l10n.getString("live-ledger-filter-type"),
        labelOverride: typeChipLabel,
        startIcon: "transfer-arrows",
        isActive: selectedType != null,
        onRemove: () => setSelectedType(null),
        renderPanel: (close, remove) => (
          <TypeFilterPanel close={close} remove={remove} onApply={resetPage} />
        ),
      },
      {
        id: "amount",
        label: l10n.getString("live-ledger-filter-amount"),
        labelOverride: amountChipLabel,
        startIcon: "dollar-sign",
        isActive: amountMin !== undefined || amountMax !== undefined,
        onRemove: clearAmountRange,
        renderPanel: (close, remove) => (
          <AmountFilterPanel
            close={close}
            remove={remove}
            onApply={resetPage}
          />
        ),
      },
    ];
  }, [
    l10n,
    selectedSavedFilter,
    savedFiltersList,
    savedFiltersLoading,
    selectSavedFilter,
    setDeleteFilterId,
    accountsChipLabel,
    accountsChipCount,
    selectedAccountIds,
    setSelectedAccountIds,
    categoryChipLabel,
    categoryChipCount,
    selectedCategories,
    setSelectedCategories,
    selectedMethods,
    setSelectedMethods,
    selectedBaiCodes,
    setSelectedBaiCodes,
    amountChipLabel,
    amountMin,
    amountMax,
    clearAmountRange,
    typeChipLabel,
    selectedType,
    setSelectedType,
    dateChipLabel,
    settledFrom,
    settledTo,
    clearDateRange,
    resetPage,
  ]);

  const filterBarFilters = useMemo(
    () =>
      filterIds
        .map((id) => availableFilters.find((filter) => filter.id === id))
        .filter((filter): filter is FilterBarFilter => filter !== undefined)
        .filter(
          (filter) =>
            filter.isActive ||
            !filtersShownOnlyWhenActive?.some((id) => id === filter.id)
        )
        .map((filter) =>
          filtersAlwaysVisible ? { ...filter, alwaysVisible: true } : filter
        ),
    [
      filterIds,
      filtersShownOnlyWhenActive,
      availableFilters,
      filtersAlwaysVisible,
    ]
  );

  // --- Save view actions ---
  let saveViewActions = null;
  if (savedFiltersEnabled && hasAnyActive && selectedSavedFilter) {
    saveViewActions = (
      <SplitButton
        kind="primary"
        size="xs"
        label={l10n.getString(
          saveAction === "save" ? "saved-filters-save" : "saved-filters-update"
        )}
        onClick={handleSaveAction}
        type="button"
      >
        <SplitButton.Menu>
          {saveAction !== "save" && (
            <SplitButton.MenuItem
              label={l10n.getString("saved-filters-save")}
              startIcon={undefined}
              endIcon={undefined}
              onSelect={() => setSaveAction("save")}
            />
          )}
          {saveAction !== "update" && (
            <SplitButton.MenuItem
              label={l10n.getString("saved-filters-update")}
              startIcon={undefined}
              endIcon={undefined}
              onSelect={() => setSaveAction("update")}
            />
          )}
        </SplitButton.Menu>
      </SplitButton>
    );
  } else if (savedFiltersEnabled && hasAnyActive) {
    saveViewActions = (
      <Button
        kind="primary"
        size="xs"
        label={l10n.getString("saved-filters-save")}
        onClick={() => setIsSaveDialogOpen(true)}
        type="button"
      />
    );
  }

  if (filterBarFilters.length === 0 && search === false) {
    return null;
  }

  return (
    <>
      <div
        className={styles.filterBarWrapper}
        data-testid="transaction-ledger-filter-bar"
      >
        <FilterBar
          searchPlaceholder={l10n.getString("live-ledger-search-placeholder")}
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          onClearFilter={handleClearFilters}
          filters={filterBarFilters}
          search={search}
          actions={saveViewActions}
          hasDividerAfterFirst={savedFiltersEnabled}
        />
      </div>
      <SaveViewDialog
        isOpen={isSaveDialogOpen}
        onClose={() => setIsSaveDialogOpen(false)}
        onSave={async (name: string) => {
          const success = await createSavedFilter(name);
          if (success) setIsSaveDialogOpen(false);
        }}
        validateName={validateSavedFilterName}
      />
      <DeleteViewDialog
        isOpen={deleteFilterId !== null}
        viewName={
          savedFiltersList.find((f) => f.id === deleteFilterId)?.name ?? ""
        }
        onClose={() => setDeleteFilterId(null)}
        onDelete={async () => {
          if (deleteFilterId) {
            await deleteSavedFilter(deleteFilterId);
            setDeleteFilterId(null);
          }
        }}
      />
    </>
  );
};

export default TransactionLedgerFilters;
